import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { catalogApi, type ProcessingQueueRow, type CreateCatalogRequest } from "@/lib/api/catalog";
import { ExistingCopyEditor } from "@/components/catalog/ExistingCopyEditor";
import { useItemTypes } from "@/hooks/useItemTypes";
import { useLocations } from "@/hooks/useLocations";
import { useCollections } from "@/hooks/useCollections";
import { useBookConditions } from "@/hooks/useBookConditions";
import { useItemStatuses } from "@/hooks/useItemStatuses";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import {
  ArrowLeft,
  BookOpen,
  Loader2,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Layers,
  ListOrdered,
  Package,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumberWithCommas } from "@/lib/formatters";

const STEPS = [
  { id: 1, label: "Queue", description: "Choose a record", icon: ListOrdered },
  { id: 2, label: "Enrich", description: "Bibliographic details", icon: BookOpen },
  { id: 3, label: "Copies", description: "Shelving & availability", icon: Package },
  { id: 4, label: "Done", description: "Review", icon: CheckCircle2 },
] as const;

type QueueState = {
  data: ProcessingQueueRow[];
  total: number;
  loading: boolean;
};

const emptyBiblio = (): CreateCatalogRequest["biblio"] => ({
  title: "",
  subtitle: "",
  authors: [],
  editors: [],
  edition: "",
  isbn: "",
  doi: "",
  publisher: "",
  placeOfPublication: "",
  publishedDate: "",
  pages: undefined,
  illustrations: "",
  size: "",
  seriesInformation: "",
  subjects: [],
  description: "",
  coverUrl: "",
  lcClassification: "",
  callNumber: "",
  bindingType: "",
});

export function AcquisitionCatalogingWizardPage() {
  const toast = useToast();
  const { hasPermission } = useAccessControl();
  const canEdit = hasPermission("catalog.edit") || hasPermission("book.edit");

  const [step, setStep] = useState(1);
  const [queuePage, setQueuePage] = useState(1);
  const pageSize = 15;
  const [purchaseOnly, setPurchaseOnly] = useState(true);
  const [queue, setQueue] = useState<QueueState>({
    data: [],
    total: 0,
    loading: false,
  });
  const [selectedBiblioId, setSelectedBiblioId] = useState<string | null>(null);
  const [biblio, setBiblio] = useState<any | null>(null);
  const [loadingBiblio, setLoadingBiblio] = useState(false);
  const [biblioForm, setBiblioForm] = useState(emptyBiblio);
  const [authorInput, setAuthorInput] = useState("");
  const [editorInput, setEditorInput] = useState("");
  const [subjectInput, setSubjectInput] = useState("");
  const [savingBiblio, setSavingBiblio] = useState(false);
  const [savingItemId, setSavingItemId] = useState<string | null>(null);

  const { data: itemTypesRaw = [] } = useItemTypes(true);
  const { data: locationsRaw = [] } = useLocations(true);
  const { data: collectionsRaw = [] } = useCollections(true);
  const { data: conditionsRaw = [] } = useBookConditions(true);
  const { data: itemStatuses = [] } = useItemStatuses();
  const { data: branches = [] } = useActiveBranches();

  const itemTypes = itemTypesRaw.filter((t) => t.isActive);
  const locations = locationsRaw.filter((l) => l.isActive);
  const collections = collectionsRaw.filter((c) => c.isActive);
  const conditions = conditionsRaw.filter((c) => c.isActive).sort((a, b) => a.order - b.order);

  const loadQueue = useCallback(async () => {
    setQueue((q) => ({ ...q, loading: true }));
    try {
      const res = await catalogApi.getProcessingQueue({
        page: queuePage,
        pageSize,
        acquisitionMode: purchaseOnly ? "PURCHASE" : undefined,
      });
      setQueue({ data: res.data, total: res.total, loading: false });
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to load processing queue");
      setQueue((q) => ({ ...q, loading: false }));
    }
  }, [queuePage, purchaseOnly, toast]);

  useEffect(() => {
    if (step === 1) {
      void loadQueue();
    }
  }, [step, loadQueue]);

  const loadBiblio = useCallback(
    async (id: string) => {
      setLoadingBiblio(true);
      try {
        const data = await catalogApi.getBiblio(id);
        setBiblio(data);
        setBiblioForm({
          title: data.title || "",
          subtitle: data.subtitle || "",
          authors: data.authors || [],
          editors: data.editors || [],
          edition: data.edition || "",
          isbn: data.isbn || "",
          doi: data.doi || "",
          publisher: data.publisher || "",
          placeOfPublication: data.placeOfPublication || "",
          publishedDate: data.publishedDate || "",
          pages: data.pages ?? undefined,
          illustrations: data.illustrations || "",
          size: data.size || "",
          seriesInformation: data.seriesInformation || "",
          subjects: data.subjects || [],
          description: data.description || "",
          coverUrl: data.coverUrl || "",
          lcClassification: data.lcClassification || "",
          callNumber: data.callNumber || "",
          bindingType: data.bindingType || "",
        });
      } catch (e: any) {
        toast.error(e.response?.data?.message || "Failed to load record");
        setStep(1);
        setSelectedBiblioId(null);
      } finally {
        setLoadingBiblio(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    if (selectedBiblioId && step >= 2 && step <= 3) {
      void loadBiblio(selectedBiblioId);
    }
  }, [selectedBiblioId, step, loadBiblio]);

  useEffect(() => {
    if (step === 4 && selectedBiblioId) {
      void loadBiblio(selectedBiblioId);
    }
  }, [step, selectedBiblioId, loadBiblio]);

  const processingItems = useMemo(
    () => (biblio?.Items ?? []).filter((it: any) => it.statusCode === "PROCESSING"),
    [biblio]
  );

  const addAuthor = () => {
    const t = authorInput.trim();
    if (t && !biblioForm.authors?.includes(t)) {
      setBiblioForm((p) => ({ ...p, authors: [...(p.authors || []), t] }));
      setAuthorInput("");
    }
  };

  const addEditor = () => {
    const t = editorInput.trim();
    if (t && !biblioForm.editors?.includes(t)) {
      setBiblioForm((p) => ({ ...p, editors: [...(p.editors || []), t] }));
      setEditorInput("");
    }
  };

  const addSubject = () => {
    const t = subjectInput.trim();
    if (t && !biblioForm.subjects?.includes(t)) {
      setBiblioForm((p) => ({ ...p, subjects: [...(p.subjects || []), t] }));
      setSubjectInput("");
    }
  };

  const startRow = (row: ProcessingQueueRow) => {
    setSelectedBiblioId(row.biblioId);
    setStep(2);
  };

  const handleSaveBiblioContinue = async () => {
    if (!selectedBiblioId || !canEdit) return;
    if (!biblioForm.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    setSavingBiblio(true);
    try {
      await catalogApi.updateBiblio(selectedBiblioId, biblioForm);
      toast.success("Bibliographic record updated");
      await loadBiblio(selectedBiblioId);
      setStep(3);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to save");
    } finally {
      setSavingBiblio(false);
    }
  };

  const handleItemSave = async (itemId: string, updates: Record<string, unknown>) => {
    if (!canEdit) return;
    setSavingItemId(itemId);
    try {
      await catalogApi.updateItem(itemId, updates as Parameters<typeof catalogApi.updateItem>[1]);
      toast.success("Copy updated");
      if (selectedBiblioId) await loadBiblio(selectedBiblioId);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to update copy");
    } finally {
      setSavingItemId(null);
    }
  };

  const goToQueue = () => {
    setStep(1);
    setSelectedBiblioId(null);
    setBiblio(null);
    setBiblioForm(emptyBiblio());
  };

  const queueColumns = useMemo<DataTableColumn<ProcessingQueueRow>[]>(
    () => [
      {
        id: "title",
        header: "Title",
        cell: (row) => (
          <div>
            <div className="font-medium text-gray-900">{row.title}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {row.authors?.length ? row.authors.join("; ") : "—"}
            </div>
          </div>
        ),
      },
      {
        id: "isbn",
        header: "ISBN",
        cell: (row) => <span className="font-mono text-sm">{row.isbn || "—"}</span>,
        width: "w-36",
      },
      {
        id: "counts",
        header: "Processing / total",
        cell: (row) => (
          <Badge variant="outline" className="font-normal">
            {formatNumberWithCommas(row.processingItemCount)} /{" "}
            {formatNumberWithCommas(row.totalItemCount)}
          </Badge>
        ),
        align: "center",
      },
      {
        id: "oldest",
        header: "Queued",
        hideOnMobile: true,
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {row.oldestProcessingAt
              ? new Date(row.oldestProcessingAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "—"}
          </span>
        ),
      },
      {
        id: "action",
        header: "",
        align: "right",
        cell: (row) => (
          <Button type="button" size="sm" className="gap-1" onClick={() => startRow(row)}>
            Start
            <ChevronRight className="h-4 w-4" />
          </Button>
        ),
        width: "w-28",
      },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil(queue.total / pageSize));

  if (loadingBiblio && step >= 2 && step <= 3 && !biblio) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingOverlay size="lg" label="Loading record…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Acquisition cataloging"
        description="Complete deferred enrichment for received copies (PROCESSING status)."
        breadcrumbItems={[
          { label: "Catalog", href: "/catalog" },
          { label: "Acquisition cataloging" },
        ]}
        showSearch={false}
      />

      {/* Stepper */}
      <Card className="border border-gray-200">
        <CardContent className="pt-6">
          <nav className="flex flex-wrap gap-2 md:gap-0 md:justify-between">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (s.id === 1) goToQueue();
                      else if (selectedBiblioId && s.id <= step) setStep(s.id);
                    }}
                    disabled={s.id !== 1 && !selectedBiblioId}
                    className={cn(
                      "flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors md:flex-1",
                      active && "border-primary bg-primary/5 ring-1 ring-primary/20",
                      done && !active && "border-green-200 bg-green-50/50",
                      !active && !done && "border-gray-200 bg-white opacity-80",
                      s.id !== 1 && !selectedBiblioId && "opacity-40 cursor-not-allowed"
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                        active && "bg-primary text-primary-foreground",
                        done && !active && "bg-green-600 text-white",
                        !active && !done && "bg-gray-200 text-gray-700"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {idx + 1}. {s.label}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{s.description}</div>
                    </div>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className="hidden md:flex items-center px-1 text-gray-300">
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        </CardContent>
      </Card>

      {/* Step 1 — Queue */}
      {step === 1 && (
        <Card className="border border-gray-200">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardList className="h-5 w-5 text-primary" />
                Processing queue
              </CardTitle>
              <CardDescription>
                Records with at least one copy awaiting cataloging after acquisition receive.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="purchase-only" className="text-sm text-gray-600 whitespace-nowrap">
                Purchase orders only
              </Label>
              <Switch
                id="purchase-only"
                checked={purchaseOnly}
                onCheckedChange={(v) => {
                  setPurchaseOnly(v);
                  setQueuePage(1);
                }}
              />
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={queueColumns}
              data={queue.data}
              rowKey={(r) => r.biblioId}
              loading={queue.loading}
              emptyState={
                <div className="py-16 text-center text-gray-500 text-sm">
                  No items in the processing queue. Receive on a PO with &quot;defer catalog
                  enrichment&quot; to add rows here.
                </div>
              }
              pagination={
                queue.total > pageSize ? (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-sm text-gray-600">
                      Page {queuePage} of {totalPages} ({formatNumberWithCommas(queue.total)}{" "}
                      records)
                    </span>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={queuePage <= 1 || queue.loading}
                        onClick={() => setQueuePage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={queuePage >= totalPages || queue.loading}
                        onClick={() => setQueuePage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : undefined
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Enrich */}
      {step === 2 && biblio && (
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg">Enrich bibliographic record</CardTitle>
            <CardDescription>
              Add classification, subjects, and other metadata before making copies available.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 max-w-3xl">
            {!canEdit && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                You do not have permission to edit catalog records. Contact an administrator.
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-2">
                <Label>Title</Label>
                <Input
                  value={biblioForm.title}
                  onChange={(e) => setBiblioForm((p) => ({ ...p, title: e.target.value }))}
                  disabled={!canEdit}
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Subtitle</Label>
                <Input
                  value={biblioForm.subtitle}
                  onChange={(e) => setBiblioForm((p) => ({ ...p, subtitle: e.target.value }))}
                  disabled={!canEdit}
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Authors</Label>
                <div className="flex gap-2">
                  <Input
                    value={authorInput}
                    onChange={(e) => setAuthorInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAuthor())}
                    placeholder="Add author"
                    disabled={!canEdit}
                  />
                  <Button type="button" variant="secondary" onClick={addAuthor} disabled={!canEdit}>
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(biblioForm.authors ?? []).map((a) => (
                    <Badge
                      key={a}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() =>
                        canEdit &&
                        setBiblioForm((p) => ({
                          ...p,
                          authors: (p.authors || []).filter((x) => x !== a),
                        }))
                      }
                    >
                      {a} ×
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>ISBN</Label>
                <Input
                  value={biblioForm.isbn}
                  onChange={(e) => setBiblioForm((p) => ({ ...p, isbn: e.target.value }))}
                  disabled={!canEdit}
                />
              </div>
              <div className="space-y-2">
                <Label>Publisher</Label>
                <Input
                  value={biblioForm.publisher}
                  onChange={(e) => setBiblioForm((p) => ({ ...p, publisher: e.target.value }))}
                  disabled={!canEdit}
                />
              </div>
              <div className="space-y-2">
                <Label>Published date</Label>
                <Input
                  value={biblioForm.publishedDate}
                  onChange={(e) => setBiblioForm((p) => ({ ...p, publishedDate: e.target.value }))}
                  disabled={!canEdit}
                />
              </div>
              <div className="space-y-2">
                <Label>Place of publication</Label>
                <Input
                  value={biblioForm.placeOfPublication}
                  onChange={(e) =>
                    setBiblioForm((p) => ({ ...p, placeOfPublication: e.target.value }))
                  }
                  disabled={!canEdit}
                />
              </div>
              <div className="space-y-2">
                <Label>LC classification</Label>
                <Input
                  value={biblioForm.lcClassification}
                  onChange={(e) =>
                    setBiblioForm((p) => ({ ...p, lcClassification: e.target.value }))
                  }
                  disabled={!canEdit}
                />
              </div>
              <div className="space-y-2">
                <Label>Call number</Label>
                <Input
                  value={biblioForm.callNumber}
                  onChange={(e) => setBiblioForm((p) => ({ ...p, callNumber: e.target.value }))}
                  disabled={!canEdit}
                />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Editors</Label>
                <div className="flex gap-2">
                  <Input
                    value={editorInput}
                    onChange={(e) => setEditorInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEditor())}
                    placeholder="Add editor"
                    disabled={!canEdit}
                  />
                  <Button type="button" variant="secondary" onClick={addEditor} disabled={!canEdit}>
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(biblioForm.editors ?? []).map((a) => (
                    <Badge
                      key={a}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() =>
                        canEdit &&
                        setBiblioForm((p) => ({
                          ...p,
                          editors: (p.editors || []).filter((x) => x !== a),
                        }))
                      }
                    >
                      {a} ×
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Subjects</Label>
                <div className="flex gap-2">
                  <Input
                    value={subjectInput}
                    onChange={(e) => setSubjectInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSubject())}
                    placeholder="Add subject heading"
                    disabled={!canEdit}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addSubject}
                    disabled={!canEdit}
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(biblioForm.subjects ?? []).map((a) => (
                    <Badge
                      key={a}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() =>
                        canEdit &&
                        setBiblioForm((p) => ({
                          ...p,
                          subjects: (p.subjects || []).filter((x) => x !== a),
                        }))
                      }
                    >
                      {a} ×
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={biblioForm.description}
                  onChange={(e) => setBiblioForm((p) => ({ ...p, description: e.target.value }))}
                  rows={4}
                  disabled={!canEdit}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to queue
              </Button>
              <Button
                type="button"
                onClick={() => void handleSaveBiblioContinue()}
                disabled={!canEdit || savingBiblio}
              >
                {savingBiblio ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save & continue to copies
              </Button>
              <Button type="button" variant="ghost" asChild>
                <Link to={`/catalog/physical/${selectedBiblioId}`} target="_blank" rel="noreferrer">
                  Open full record
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Copies */}
      {step === 3 && biblio && (
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Layers className="h-5 w-5 text-primary" />
              Copies in processing
            </CardTitle>
            <CardDescription>
              Set branch, location, and collection, then set status to Available (or another shelved
              state) for each copy.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!canEdit && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                You do not have permission to edit item copies.
              </p>
            )}
            {processingItems.length === 0 ? (
              <div className="rounded-lg border border-green-200 bg-green-50/60 px-4 py-6 text-center text-sm text-green-900">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-600" />
                All copies for this title are out of PROCESSING. Continue to finish.
              </div>
            ) : (
              <div className="space-y-3">
                {processingItems.map((item: any) => (
                  <ExistingCopyEditor
                    key={item.id}
                    item={item}
                    branches={branches}
                    itemTypes={itemTypes}
                    locations={locations}
                    collections={collections}
                    conditions={conditions}
                    itemStatuses={itemStatuses}
                    savingItemId={savingItemId}
                    canEdit
                    canDelete={false}
                    onSave={(u) => handleItemSave(item.id, u)}
                    onItemUpdated={() => undefined}
                    onItemRemoved={() => undefined}
                  />
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button type="button" onClick={() => setStep(4)}>
                Finish & review
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Done */}
      {step === 4 && selectedBiblioId && biblio && (
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg">Review</CardTitle>
            <CardDescription>Summary for this cataloging session.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingBiblio ? (
              <div className="py-12 flex justify-center">
                <LoadingOverlay label="Refreshing…" />
              </div>
            ) : (
              <>
                {processingItems.length === 0 ? (
                  <div className="flex gap-3 rounded-lg border border-green-200 bg-green-50/70 p-4">
                    <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600" />
                    <div>
                      <p className="font-medium text-green-900">Cataloging complete</p>
                      <p className="text-sm text-green-800 mt-1">
                        No copies remain in PROCESSING for this title.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50/70 p-4">
                    <Package className="h-6 w-6 shrink-0 text-amber-700" />
                    <div>
                      <p className="font-medium text-amber-950">Copies still in processing</p>
                      <p className="text-sm text-amber-900 mt-1">
                        {formatNumberWithCommas(processingItems.length)} copy(ies) still have
                        PROCESSING status. Return to step 3 to finish shelving and availability.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => setStep(3)}
                      >
                        Back to copies
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={goToQueue}>
                    Process another title
                  </Button>
                  <Button type="button" variant="outline" asChild>
                    <Link to={`/catalog/physical/${selectedBiblioId}`}>View in catalog</Link>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
